import React, { useEffect, useMemo, useState } from 'react';
import { Building2, ChevronDown, Search } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { toast } from 'sonner';
import { connectSocket, disconnectSocket } from '@/Redux/Action/socketActions';
import { CommanServices } from '@/services/commanServices';
import { SaaSServices } from '@/services/saasServices';
import {
  activeInstitutionId,
  apiErrorMessage,
  extractAuthenticationResult,
  isPlatformSuperadmin,
  persistAuthenticationResult,
  readStoredInstitution,
  readStoredUser,
  unwrapApiData,
} from '@/auth/saasSession';
import {
  readSaaSInstitutionSelection,
  SAAS_INSTITUTION_SELECTION_EVENT,
  selectSaaSInstitution,
} from '../auth/saasControlPlaneSelection';
import { isGlobalMarketplaceSearch } from '../page/saasAdministrationNavigation';

const SAAS_INSTITUTION_TITLE_EVENT = 'disasterms:saas-institution-title-changed';
const SAAS_INSTITUTION_TITLE_KEY = 'disasterms_saas_selected_institution';

const announceSaaSInstitutionTitle = (institution) => {
  if (typeof window === 'undefined' || !institution) return;
  const detail = {
    institutionId: String(institution.id || ''),
    displayName: institution.display_name || institution.slug || institution.id || 'Institution',
  };
  window.localStorage.setItem(SAAS_INSTITUTION_TITLE_KEY, JSON.stringify(detail));
  window.dispatchEvent(new window.CustomEvent(SAAS_INSTITUTION_TITLE_EVENT, { detail }));
};

export function SaaSInstitutionsNavigation({
  collapsed = false,
  searchValue,
  onSearchChange,
  showSearch = true,
  language = 'en',
}) {
  const [institutions, setInstitutions] = useState([]);
  const [internalSearch, setInternalSearch] = useState('');
  const [selectedId, setSelectedId] = useState(() => readSaaSInstitutionSelection());
  const [loadingInstitutions, setLoadingInstitutions] = useState(true);
  const [institutionsError, setInstitutionsError] = useState('');
  const search = searchValue === undefined ? internalSearch : searchValue;
  const setSearch = onSearchChange || setInternalSearch;
  const isArabic = language === 'ar';

  useEffect(() => {
    let cancelled = false;
    const refreshInstitutions = async ({ initial = false } = {}) => {
      if (initial && !cancelled) setLoadingInstitutions(true);
      try {
        const response = await SaaSServices.listInstitutions();
        const rows = unwrapApiData(response);
        if (!Array.isArray(rows)) throw new Error('Invalid institution directory response');
        if (cancelled) return;
        const sortedRows = [...rows].sort((a, b) => String(a.display_name || a.slug || '').localeCompare(String(b.display_name || b.slug || '')));
        const requested = readSaaSInstitutionSelection();
        const requestedInstitution = sortedRows.find((row) => String(row.id) === String(requested));
        setInstitutions(sortedRows);
        setInstitutionsError('');
        if (requestedInstitution) announceSaaSInstitutionTitle(requestedInstitution);
        setSelectedId((current) => {
          if (requestedInstitution) return requested;
          if (current && sortedRows.some((row) => String(row.id) === String(current))) return current;
          return '';
        });
      } catch (_) {
        if (!cancelled) setInstitutionsError(isArabic ? 'تعذر تحميل المؤسسات.' : 'Unable to load institutions.');
      } finally {
        if (!cancelled) setLoadingInstitutions(false);
      }
    };
    const handleSelection = (event) => setSelectedId(String(event.detail?.institutionId || ''));
    refreshInstitutions({ initial: true });
    const timer = window.setInterval(refreshInstitutions, 30000);
    window.addEventListener('focus', refreshInstitutions);
    window.addEventListener('disasterms:saas-institutions-changed', refreshInstitutions);
    window.addEventListener(SAAS_INSTITUTION_SELECTION_EVENT, handleSelection);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      window.removeEventListener('focus', refreshInstitutions);
      window.removeEventListener('disasterms:saas-institutions-changed', refreshInstitutions);
      window.removeEventListener(SAAS_INSTITUTION_SELECTION_EVENT, handleSelection);
    };
  }, [isArabic]);

  const normalizedSearch = search.trim().toLocaleLowerCase();
  const visibleInstitutions = institutions.filter((institution) => {
    if (!normalizedSearch) return true;
    return [institution.display_name, institution.legal_name, institution.slug, institution.status]
      .some((value) => String(value || '').toLocaleLowerCase().includes(normalizedSearch));
  });

  const openInstitution = (event, institution, href) => {
    announceSaaSInstitutionTitle(institution);
    if (!window.location.pathname.startsWith('/saas-administration')) return;
    event.preventDefault();
    setSelectedId(institution.id);
    const onGlobalMarketplace = isGlobalMarketplaceSearch(window.location.search);
    if (onGlobalMarketplace) {
      window.history.pushState(window.history.state, '', href);
      window.dispatchEvent(new window.PopStateEvent('popstate'));
      selectSaaSInstitution(institution.id, { updateUrl: false });
      return;
    }
    selectSaaSInstitution(institution.id);
  };

  if (collapsed) {
    return (
      <section
        className="mt-4 space-y-2 border-t border-white/[0.08] pt-4"
        aria-label={isArabic ? 'المؤسسات' : 'Institutions'}
        data-testid="saas-institutions-navigation"
      >
        {visibleInstitutions.map((institution) => {
          const selected = String(institution.id) === String(selectedId)
            && String(readSaaSInstitutionSelection()) === String(institution.id)
            && !isGlobalMarketplaceSearch(window.location.search);
          const href = `/saas-administration?institution=${encodeURIComponent(institution.id)}`;
          const name = institution.display_name || institution.slug || institution.id;
          const initial = String(name || 'I').trim().charAt(0).toLocaleUpperCase();
          return (
            <a
              key={institution.id}
              href={href}
              onClick={(event) => openInstitution(event, institution, href)}
              title={name}
              aria-label={name}
              aria-current={selected ? 'page' : undefined}
              className={`mx-auto flex h-11 w-11 items-center justify-center rounded-[9px] border text-[12px] font-bold transition-colors ${selected
                ? 'border-[#28ade3]/40 bg-[#28ade3]/10 text-[#28ade3]'
                : 'border-[#96a5d2]/20 text-[#5f6890] hover:border-white/20 hover:bg-white/[0.035] hover:text-[#d7dcef]'}`}
            >
              {initial}
            </a>
          );
        })}
      </section>
    );
  }

  return (
    <section
      className="mt-5 border-t border-white/[0.08] pt-4"
      aria-label={isArabic ? 'المؤسسات' : 'Institutions'}
      data-testid="saas-institutions-navigation"
    >
      <div className="flex items-center gap-2.5 px-3">
        <Building2 className="h-4 w-4 shrink-0 text-[#5f6890]" strokeWidth={1.8} />
        <span className="min-w-0">
          <span className="block text-[10px] font-bold uppercase tracking-[0.16em] text-[#8a92b0]">{isArabic ? 'المؤسسات' : 'Institutions'}</span>
          <span className="mt-0.5 block text-[10px] text-[#5f6890]">
            {isArabic
              ? `${institutions.length} مؤسسة مفعلة`
              : `${institutions.length} tenant${institutions.length === 1 ? '' : 's'} provisioned`}
          </span>
        </span>
      </div>
      {showSearch && (
        <label className="relative mt-3 block px-2">
          <span className="sr-only">{isArabic ? 'بحث في المؤسسات' : 'Search institutions'}</span>
          <Search className={`pointer-events-none absolute top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[#5f6890] ${isArabic ? 'right-5' : 'left-5'}`} />
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={isArabic ? 'بحث في المؤسسات' : 'Search institutions'}
            aria-label={isArabic ? 'بحث في المؤسسات' : 'Search institutions'}
            className={`h-9 w-full rounded-[9px] border border-[#96a5d2]/20 bg-[#0a0e24] py-2 text-xs text-[#d7dcef] outline-none transition placeholder:text-[#5f6890] focus:border-[#28ade3] focus:ring-2 focus:ring-[#28ade3]/15 ${isArabic ? 'pr-8 pl-3' : 'pl-8 pr-3'}`}
          />
        </label>
      )}
      <div className="mt-3 space-y-1" data-testid="saas-institution-list">
        {visibleInstitutions.map((institution) => {
          const selected = String(institution.id) === String(selectedId)
            && String(readSaaSInstitutionSelection()) === String(institution.id)
            && !isGlobalMarketplaceSearch(window.location.search);
          const href = `/saas-administration?institution=${encodeURIComponent(institution.id)}`;
          return (
            <a
              key={institution.id}
              href={href}
              onClick={(event) => openInstitution(event, institution, href)}
              aria-current={selected ? 'page' : undefined}
              className={`block rounded-[9px] border px-3 py-2.5 text-start transition-colors ${selected
                ? 'border-[#28ade3]/40 bg-[#28ade3]/10 text-[#d7dcef]'
                : 'border-transparent text-[#8a92b0] hover:border-white/10 hover:bg-white/[0.035] hover:text-[#d7dcef]'}`}
            >
              <span className="flex min-w-0 items-center justify-between gap-2">
                <span className="truncate text-[12px] font-medium">{institution.display_name || institution.slug || institution.id}</span>
                <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${institution.status === 'active' ? 'bg-[#3cb371]' : 'bg-[#ffc000]'}`} aria-hidden="true" />
              </span>
              <span className="mt-1 block truncate text-[10px] text-[#5f6890]">{institution.slug || institution.status || (isArabic ? 'مؤسسة' : 'Institution')}</span>
            </a>
          );
        })}
        {loadingInstitutions && (
          <p className="px-3 py-3 text-xs text-[#5f6890]" role="status">
            {isArabic ? 'جارٍ تحميل المؤسسات…' : 'Loading institutions…'}
          </p>
        )}
        {!loadingInstitutions && institutionsError && (
          <p className="px-3 py-3 text-xs text-[#ef3937]" role="alert">{institutionsError}</p>
        )}
        {!loadingInstitutions && !institutionsError && !visibleInstitutions.length && (
          <p className="px-3 py-3 text-xs text-[#5f6890]">{isArabic ? 'لا توجد مؤسسات مطابقة.' : 'No matching institutions.'}</p>
        )}
      </div>
    </section>
  );
}

export default function InstitutionSwitcher({
  onActiveInstitutionChange,
  variant = 'header',
  institutionAccountsOnly = false,
  collapsed = false,
  onWorkspaceOpen,
}) {
  const dispatch = useDispatch();
  const [institutions, setInstitutions] = useState([]);
  const storedUser = readStoredUser();
  const platformAccount = isPlatformSuperadmin(storedUser);
  const [currentId, setCurrentId] = useState(() => activeInstitutionId(storedUser));
  const [switching, setSwitching] = useState(false);
  const storedInstitution = readStoredInstitution();

  useEffect(() => {
    if (institutionAccountsOnly && platformAccount) return undefined;
    let cancelled = false;
    SaaSServices.listMyInstitutions().then((response) => {
      if (cancelled) return;
      const data = unwrapApiData(response);
      if (!data) return;
      const availableInstitutions = Array.isArray(data) ? data : (data.institutions || []);
      const nextCurrentId = data.current_institution_id || activeInstitutionId(readStoredUser());
      const activeInstitution = availableInstitutions.find(
        (institution) => String(institution.id) === String(nextCurrentId),
      );
      setInstitutions(availableInstitutions);
      setCurrentId(nextCurrentId);
      if (activeInstitution) {
        localStorage.setItem('active_institution', JSON.stringify(activeInstitution));
        onActiveInstitutionChange?.(activeInstitution);
      }
    });
    return () => { cancelled = true; };
  }, [institutionAccountsOnly, onActiveInstitutionChange, platformAccount]);

  const availableInstitutions = useMemo(
    () => institutions.filter((institution) => institution.web_enabled !== false),
    [institutions],
  );

  const current = useMemo(() => (
    availableInstitutions.find((institution) => String(institution.id) === String(currentId))
    || storedInstitution
    || { id: currentId, display_name: currentId }
  ), [availableInstitutions, currentId, storedInstitution]);

  const profileVariant = variant === 'profile';
  const sidebarVariant = variant === 'sidebar';

  // Institution members switch from their profile so the header never
  // duplicates that control. Platform accounts retain their control-plane
  // header behavior, but do not receive a membership switcher in Profile.
  if (!profileVariant && !platformAccount) return null;
  if (profileVariant && (platformAccount || availableInstitutions.length < 2)) return null;
  if (!profileVariant && !sidebarVariant && !currentId && availableInstitutions.length < 2) return null;

  const openWorkspace = () => {
    if (onWorkspaceOpen) {
      onWorkspaceOpen();
      return;
    }
    window.location.assign(platformAccount ? '/saas-administration' : '/dashboard');
  };

  const switchInstitution = async (institutionId) => {
    if (!institutionId || switching) return;
    if (String(institutionId) === String(currentId)) {
      if (sidebarVariant) openWorkspace();
      return;
    }
    setSwitching(true);
    const response = await SaaSServices.switchInstitution(institutionId);
    const result = extractAuthenticationResult(response);
    if (!result || result.type !== 'authenticated') {
      setSwitching(false);
      toast.error(apiErrorMessage(response, 'Institution switch failed'));
      return;
    }
    const selected = availableInstitutions.find(
      (institution) => String(institution.id) === String(institutionId),
    );
    dispatch(disconnectSocket());
    persistAuthenticationResult(result, selected);
    CommanServices.setToken(result.accessToken);
    dispatch(connectSocket(result.accessToken));
    openWorkspace();
  };

  if (sidebarVariant) {
    return (
      <section
        className={collapsed ? 'mt-2' : 'mt-2 pb-3'}
        aria-label="Institutions"
        data-testid="saas-sidebar-institutions"
      >
        <button
          type="button"
          onClick={openWorkspace}
          title={collapsed ? 'Institutions' : undefined}
          className={`flex w-full items-center rounded-lg text-left transition-colors hover:bg-gray-100 ${collapsed ? 'justify-center px-2 py-2.5' : 'gap-2.5 px-3 py-2'}`}
        >
          <Building2 className="h-4 w-4 shrink-0 text-gray-600" />
          {!collapsed && <span className="min-w-0"><span className="block text-sm font-medium text-gray-700">Institutions</span><span className="block text-[11px] text-gray-400">{availableInstitutions.length} tenant{availableInstitutions.length === 1 ? '' : 's'} provisioned</span></span>}
        </button>
      </section>
    );
  }

  const selector = (
    <div className="relative" title="Active institution">
      <Building2 className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-blue-200" />
      {availableInstitutions.length > 1 ? (
        <>
          <select
            value={currentId}
            onChange={(event) => switchInstitution(event.target.value)}
            disabled={switching}
            className="h-10 max-w-56 appearance-none rounded-lg border border-white/20 bg-white/10 py-2 pl-9 pr-8 text-sm font-medium text-white outline-none hover:bg-white/15 disabled:opacity-60"
            aria-label="Active institution"
          >
            {availableInstitutions.map((institution) => (
              <option key={institution.id} value={institution.id} className="text-slate-900">{institution.display_name || institution.slug || institution.id}</option>
            ))}
          </select>
          <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-blue-200" />
        </>
      ) : (
        <span className="inline-flex h-10 max-w-56 items-center truncate rounded-lg border border-white/20 bg-white/10 py-2 pl-9 pr-3 text-sm font-medium text-white">
          {current?.display_name || current?.slug || currentId}
        </span>
      )}
    </div>
  );

  if (profileVariant) {
    return (
      <section
        className="bg-white border border-gray-200 rounded-xl shadow-sm p-6"
        data-testid="profile-institution-switcher"
      >
        <h3 className="text-lg font-semibold text-gray-900">Institutions</h3>
        <p className="mt-1 text-sm leading-5 text-gray-500">
          Switch to another institution to use its permissions and data.
        </p>
        <div className="mt-4">
          <label htmlFor="profile-active-institution" className="block text-sm font-medium text-gray-700">
            Active institution
          </label>
          <div className="relative mt-1">
            <Building2 className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <select
              id="profile-active-institution"
              value={currentId}
              onChange={(event) => switchInstitution(event.target.value)}
              disabled={switching}
              className="h-10 w-full appearance-none rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-9 text-sm font-medium text-gray-900 outline-none transition-colors hover:border-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:cursor-wait disabled:opacity-60"
              aria-label="Active institution"
            >
              {availableInstitutions.map((institution) => (
                <option key={institution.id} value={institution.id}>
                  {institution.display_name || institution.slug || institution.id}
                </option>
              ))}
            </select>
            <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          </div>
          <p className="mt-2 text-xs text-gray-500" aria-live="polite">
            {switching
              ? 'Opening institution…'
              : `Currently using ${current?.display_name || current?.slug || currentId}`}
          </p>
        </div>
      </section>
    );
  }

  return <div className="hidden lg:block">{selector}</div>;
}
